/**
 * PATH: packages/ui/src/components/settings/appearance-tab/DensitySelector.tsx
 * PURPOSE: Display density options — compact mode and reduce motion toggles
 * WHY: Extracted from AppearanceTab to keep orchestrator under 300 lines
 *
 * FLOW:
 * ┌──────────────────────┐    ┌──────────────────┐    ┌──────────────────┐
 * │ compactMode/motion   │ →  │ toggle switches   │ →  │ onToggle*()      │
 * └──────────────────────┘    └──────────────────┘    └──────────────────┘
 *
 * DEPENDENCIES:
 *  - cn utility: conditional class merging
 */

import { cn } from '../../../lib/utils'

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface DensitySelectorProps {
  compactMode?: boolean
  reduceMotion?: boolean
  onToggleCompact: () => void
  onToggleMotion: () => void
}

// ─────────────────────────────────────────────────────────────
// Toggle (private helper)
// ─────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  onToggle,
}: {
  label: string
  description: string
  checked?: boolean
  onToggle: () => void
}) {
  return (
    <label className="flex items-center justify-between gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 cursor-pointer">
      <div>
        <span className="font-medium text-slate-900 dark:text-white">
          {label}
        </span>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={onToggle}
        className={cn(
          'relative w-11 h-6 rounded-full transition-colors',
          checked ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            checked && 'translate-x-5'
          )}
        />
      </button>
    </label>
  )
}

// ─────────────────────────────────────────────────────────────
// DensitySelector
// ─────────────────────────────────────────────────────────────

export function DensitySelector({
  compactMode,
  reduceMotion,
  onToggleCompact,
  onToggleMotion,
}: DensitySelectorProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">
        Display Options
      </h3>
      <div className="space-y-4">
        <ToggleRow
          label="Compact Mode"
          description="Reduce spacing for more content"
          checked={compactMode}
          onToggle={onToggleCompact}
        />
        <ToggleRow
          label="Reduce Motion"
          description="Minimize animations and transitions"
          checked={reduceMotion}
          onToggle={onToggleMotion}
        />
      </div>
    </div>
  )
}
